import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { commandCenterApi, feedOpsApi } from '../services/api'

export function useFeedOpsOrderMutations({
  slug, surface, offering, refreshQueue,
  closePlacement, clearAllStates, clearOverviewDraft, formatBulkReason,
}) {
  const saveOrderMutation = useMutation({
    mutationFn: ({ items, replaceExisting = false }) => feedOpsApi.upsertOrder(
      slug,
      { surface, offering_id: offering, replace_existing: replaceExisting, items },
    ),
    onSuccess: (_result, variables) => {
      toast.success('Queue ordering saved')
      if (variables?.closePlacement) closePlacement()
      refreshQueue()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to save queue ordering'),
  })

  // Command Center has no per-organization order endpoint to call: the
  // same drag/keyboard draft is saved as one provider-scoped `set_order`
  // bulk command, validated all-or-nothing against every target. A
  // rejected save deliberately keeps the unsaved draft (and the sticky
  // bar) so the operator can retry or discard, matching how every other
  // rejected bulk command leaves the list untouched.
  const saveCommandCenterOrderMutation = useMutation({
    mutationFn: (envelope) => commandCenterApi.createBulkCommand(envelope),
    onSuccess: (_result, envelope) => {
      const stateCount = new Set(envelope.targets.map((target) => target.offering_id)).size
      toast.success(stateCount > 1
        ? `Queue ordering saved for ${stateCount} states`
        : 'Queue ordering saved')
      clearAllStates()
      refreshQueue()
    },
    onError: (error) => {
      const reason = error.response?.status === 422
        ? error.response.data?.reasons?.[0]
        : null
      toast.error(reason
        ? `Order not saved: ${formatBulkReason(reason)}`
        : (error.response?.data?.error || 'Failed to save queue ordering'))
    },
  })

  const resetOrderMutation = useMutation({
    mutationFn: (item) => feedOpsApi.resetOrder(
      slug,
      item.id,
      { surface, ...(offering ? { offering } : {}) },
    ),
    onSuccess: () => {
      toast.success('Manual order reset')
      closePlacement()
      refreshQueue()
    },
    onError: () => toast.error('Failed to reset manual order'),
  })

  const resetSurfaceOrderMutation = useMutation({
    mutationFn: () => feedOpsApi.resetSurfaceOrder(
      slug,
      { surface, ...(offering ? { offering } : {}) },
    ),
    onSuccess: () => {
      toast.success('Automatic ordering restored')
      refreshQueue()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to restore automatic ordering'),
  })

  const saveOverviewOrderMutation = useMutation({
    mutationFn: ({ offset, families }) => feedOpsApi.upsertOverviewOrder(slug, { surface, offset, families }),
    onSuccess: (result) => {
      const updated = result?.updated_offerings ?? 0
      toast.success(`Order applied to ${updated} ${updated === 1 ? 'regulation' : 'regulations'}`)
      clearOverviewDraft()
      refreshQueue()
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Failed to apply the order'),
  })

  return {
    saveOrderMutation, saveCommandCenterOrderMutation, resetOrderMutation,
    resetSurfaceOrderMutation, saveOverviewOrderMutation,
  }
}
